import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle,
  Mail,
  User,
  MessageSquare,
  Loader,
  ShieldCheck,
  MapPin,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { PublicHeader } from '../components/PublicHeader';
import { useForceLightMode } from '../hooks/useForceLightMode';

export const ContactPage: React.FC = () => {
  useForceLightMode();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
      const response = await fetch(`${API_BASE_URL}/notifications/contact/general`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30">
      <PublicHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Info */}
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-medical-900 mb-6 leading-tight tracking-tight">
              Get in touch
            </h1>
            <p className="text-xl text-medical-600 mb-10 leading-relaxed font-light">
              Have a question, need support, or want to learn more about ClinLexis? We'd love to hear from you.
            </p>

            <div className="space-y-6 mb-10">
              {[
                {
                  icon: Mail,
                  title: 'Quick response',
                  description: 'We aim to respond to all enquiries within one working day.',
                },
                {
                  icon: MessageSquare,
                  title: 'Technical support',
                  description: 'Our team can help with account issues, platform questions, and feature requests.',
                },
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-medical-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-medical-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              {[
                { icon: ShieldCheck, label: 'NHS DSPT Compliant' },
                { icon: MapPin, label: 'UK Data Residency' },
              ].map((badge, index) => (
                <span
                  key={index}
                  className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs font-medium text-green-700"
                >
                  <badge.icon className="h-3 w-3" />
                  <span>{badge.label}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-medical-200/50 p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-medical-900 mb-3">Message sent!</h2>
                <p className="text-medical-600 mb-6">
                  Thank you for getting in touch. We'll get back to you as soon as possible.
                </p>
                <Link
                  to="/"
                  className="text-primary-600 hover:text-primary-700 font-semibold transition-colors"
                >
                  Back to home
                </Link>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-medical-900 mb-2">Send us a message</h2>
                <p className="text-medical-600 mb-8">Fill in your details and we'll get back to you shortly.</p>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-medical-700 mb-1.5">
                      Full name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-medical-400" />
                      <input
                        id="name"
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-medical-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-medical-900 placeholder-medical-400"
                        placeholder="Dr. Jane Smith"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-medical-700 mb-1.5">
                      Email address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-medical-400" />
                      <input
                        id="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-medical-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-medical-900 placeholder-medical-400"
                        placeholder="jane@example.co.uk"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-medical-700 mb-1.5">
                      Message
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-medical-400" />
                      <textarea
                        id="message"
                        rows={5}
                        required
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-medical-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-medical-900 placeholder-medical-400 resize-none"
                        placeholder="How can we help?"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary py-3.5 text-base flex items-center justify-center space-x-2 group shadow-lg shadow-primary-200/50 hover:shadow-xl hover:shadow-primary-300/50 transition-all disabled:opacity-60"
                  >
                    {loading ? (
                      <Loader className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <span>Send message</span>
                        <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-medical-200 mt-16 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-2">
          <p className="text-sm text-medical-400 text-center">
            &copy; {new Date().getFullYear()} ClinLexis&trade;. All rights reserved.
          </p>
          <p className="text-xs text-medical-500 text-center">
            ClinLexis Ltd &middot; Company No. 17081483 &middot; Registered in England and Wales &middot; 238a Telegraph Road, Heswall, Wirral, CH60 0AL
          </p>
        </div>
      </footer>
    </div>
  );
};
